import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { authApi } from "@/lib/api";
import Seal from "@/components/Seal";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, RotateCw } from "lucide-react";

const VerifyEmail = () => {
  const { verifyEmail } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [status, setStatus] = useState("verifying"); // verifying | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const [email, setEmail] = useState("");
  const [resending, setResending] = useState(false);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) { setStatus("error"); setErrorMsg("No verification token was provided."); return; }
    (async () => {
      try {
        const user = await verifyEmail(token);
        setStatus("success");
        toast.success("Email verified — welcome to WECH-RB");
        setTimeout(() => navigate(user.role === "admin" ? "/admin" : "/dashboard", { replace: true }), 1400);
      } catch (err) {
        setStatus("error");
        setErrorMsg(err.response?.data?.detail || "This verification link is invalid or has expired.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const resend = async () => {
    if (!email) { toast.error("Enter your email to resend"); return; }
    setResending(true);
    try {
      await authApi.resendVerification(email);
      toast.success("If that account needs verification, a new link is on its way");
    } catch {
      toast.error("Could not resend right now");
    } finally { setResending(false); }
  };

  return (
    <div className="wechr-container flex min-h-[70vh] flex-col items-center justify-center py-12">
      <Card className="w-full max-w-md" data-testid="verify-email-card">
        <CardHeader className="items-center text-center">
          <Seal size={40} />
          <CardTitle className="mt-2 font-heading text-xl">Email verification</CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          {status === "verifying" && (
            <div className="flex flex-col items-center gap-3 py-6" data-testid="verify-status-verifying">
              <Loader2 className="h-8 w-8 animate-spin text-wgreen-600" />
              <p className="text-sm text-[var(--wechr-muted-text)]">Verifying your email address…</p>
            </div>
          )}
          {status === "success" && (
            <div className="flex flex-col items-center gap-3 py-6" data-testid="verify-status-success">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-wgreen-100"><CheckCircle2 className="h-7 w-7 text-wgreen-700" /></div>
              <p className="font-heading font-semibold text-navy-900">Your email is verified</p>
              <p className="text-sm text-[var(--wechr-muted-text)]">Redirecting you to your dashboard…</p>
            </div>
          )}
          {status === "error" && (
            <div className="flex flex-col items-center gap-3 py-4" data-testid="verify-status-error">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#FEE4E2]"><XCircle className="h-7 w-7 text-[#B42318]" /></div>
              <p className="font-heading font-semibold text-navy-900">{/expired/i.test(errorMsg) ? "This link has expired" : "This link is invalid or expired"}</p>
              <p className="text-sm text-[var(--wechr-muted-text)]">{errorMsg || "Enter your email and we'll send a fresh verification link."} Enter your email below and we'll send a fresh link.</p>
              <div className="mt-2 w-full space-y-2">
                <Input type="email" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="verify-resend-email-input" />
                <Button className="btn-green w-full" onClick={resend} disabled={resending} data-testid="verify-resend-button">
                  {resending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCw className="mr-2 h-4 w-4" />} Resend verification email
                </Button>
                <Link to="/login" className="block text-sm text-wgreen-700 hover:underline">Back to sign in</Link>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmail;
